import { useQuery } from '@tanstack/react-query';
import { api } from '../api/cliente';

/**
 * Consultas compartilhadas do portal.
 *
 * A tela inicial já traz o funil com a CHAVE, o RÓTULO e a COR de todas as fases.
 * As demais telas reaproveitam essa mesma consulta em vez de repetir a tabela de
 * rótulos no React: a rota `/devedores/fases`, que a equipe usa, é do escritório e
 * a clínica não a alcança — e duplicar rótulo e cor aqui criaria duas listas para
 * manter em sincronia, que é exatamente como uma fase nova acaba aparecendo sem cor.
 */

export interface FaseDoPortal {
  readonly fase: string;
  readonly rotulo: string;
  readonly cor: string;
  readonly quantidade: number;
}

export interface InicioDoPortal {
  readonly carteira: { devedores: number; valorTotal: number };
  /** Um por carteira do acesso. O fim é calculado no servidor, nunca aqui. */
  readonly contratos: Array<{
    clinica: string;
    assinadoEm: string | null;
    meses: number | null;
    terminaEm: string | null;
    diasRestantes: number | null;
    situacao: 'sem_contrato' | 'vigente' | 'vencendo' | 'vencido';
  }>;
  readonly pendencias: {
    semTelefone: number;
    aguardandoAssinatura: number;
  };
  readonly funil: FaseDoPortal[];
  readonly financeiro: {
    totalRecebido: number;
    permaneceComAClinica: number;
    pagamentos: number;
    /** Subtítulo do cartão "Recebido no total". */
    legendaDoTotal: string;
    /** Meses em que houve pagamento, do mais recente para o mais antigo. */
    mesesDisponiveis: string[];
    noMes: {
      mes: string;
      nome: string;
      recebido: number;
      permaneceComAClinica: number;
      /** Só o êxito. Mensalidade e custas não aparecem no portal. */
      honorariosExito: number;
      pagamentos: number;
    };
  } | null;
}

/**
 * O `mes` afeta só o bloco financeiro; o resto da resposta é sempre a situação de
 * hoje. A chave `'atual'` mantém as telas que chamam sem argumento — `ClientesPortal`
 * e `FichaPortal`, que só querem o rótulo e a cor das fases — no mesmo cache.
 *
 * `placeholderData` segura o conteúdo anterior enquanto o mês novo carrega: sem ele
 * a tela pisca em branco a cada troca no seletor.
 */
export function useInicioDoPortal(mes?: string) {
  return useQuery({
    queryKey: ['portal', 'inicio', mes ?? 'atual'],
    queryFn: () => api.buscar<InicioDoPortal>('/portal/inicio', mes ? { mes } : undefined),
    placeholderData: (anterior) => anterior,
  });
}

/** Rótulo e cor de cada fase, indexados pela chave. */
export function useFasesDoPortal(): Map<string, FaseDoPortal> {
  const { data } = useInicioDoPortal();
  return new Map((data?.funil ?? []).map((f) => [f.fase, f]));
}
